import React, { useMemo } from 'react'
import { H1, useContainerWidth, Separator, LayoutBand, Card, H2 } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { useGroupsContext } from '@fs/zion-tree-groups'
import AllTreesData from '../tree-import/components/cets/AllTreesData'
import UploadGedcomCard from '../tree-import/components/manage-trees/UploadGedcomCard'
import { sortAndFilterGroups } from '../services/getGroupsList'
import ManageTreeGroup from '../tree-import/components/manage-trees/ManageTreeGroup'
import CenteredLoadingSpinner from '../components/CenteredLoadingSpinner'
import useGenealogyTrees from '../tree-import/components/genealogy-trees/useGenealogyTrees'
import GenealogyMobileTable from '../tree-import/components/genealogy-trees/genealogy-table/GenealogyMobileTable'
import GenealogyTable from '../tree-import/components/genealogy-trees/genealogy-table/GenealogyTable'
import GenealogyTreesProvider from '../tree-import/components/genealogy-trees/GenealogyTreesProvider'

function ManageTreesContent({ isMobile, canAccessCET, canAccessFGT }) {
  const { t } = useTranslation()
  const { groups } = useGroupsContext()

  const { sortedResearchTreeGroups, sortedFamilyGroups, ownedTrees, sharedTrees } = useMemo(() => {
    const researchGroups = sortAndFilterGroups({
      groups: groups || [],
    })
    const familyGroups = sortAndFilterGroups({
      groups: groups || [],
      isFamily: true,
    })

    const { owned, shared } = researchGroups.reduce(
      (acc, group) => {
        if (group.memberStatus === 'ADMIN') {
          acc.owned.push(group)
        } else if (group.memberStatus === 'MEMBER') {
          acc.shared.push(group)
        }
        return acc
      },
      { owned: [], shared: [] }
    )

    return {
      sortedResearchTreeGroups: researchGroups,
      sortedFamilyGroups: familyGroups,
      ownedTrees: owned,
      sharedTrees: shared,
    }
  }, [groups])

  const hasOnlyFamilyGroups = sortedFamilyGroups.length > 0 && sortedResearchTreeGroups.length === 0
  const hasOnlySharedTrees = ownedTrees.length === 0 && sharedTrees.length > 0
  const showUploadGedcomCard = !groups?.length || hasOnlyFamilyGroups || hasOnlySharedTrees

  return (
    <>
      {showUploadGedcomCard && (
        <>
          <UploadGedcomCard />
          <Separator size="xs" />
        </>
      )}

      {canAccessFGT && sortedFamilyGroups.length > 0 && (
        <>
          <ManageTreeGroup
            groupTitle={t('tree-import.manage-trees.family-groups-heading')}
            groups={sortedFamilyGroups}
            isMobile={isMobile}
          />
          <Separator size="xs" />
        </>
      )}

      {canAccessCET && sortedResearchTreeGroups.length > 0 && (
        <>
          {ownedTrees.length > 0 && (
            <>
              <ManageTreeGroup
                groupTitle={t('tree-import.manage-trees.owned-groups-heading')}
                groups={ownedTrees}
                isMobile={isMobile}
                isOwnedTreesGroup
              />
              <Separator size="xs" />
            </>
          )}
          {sharedTrees.length > 0 && (
            <>
              <ManageTreeGroup
                groupTitle={t('tree-import.manage-trees.shared-groups-heading')}
                groups={sharedTrees}
                isMobile={isMobile}
              />
              <Separator size="xs" />
            </>
          )}
        </>
      )}
    </>
  )
}

export default function ManageTreesPage() {
  const { t } = useTranslation()
  const atWidth = useContainerWidth()
  const isMobile = atWidth({ default: true, lg: false })
  const { groups, loading, canAccessFGT, canAccessCET } = useGroupsContext()
  const { trees } = useGenealogyTrees()
  const genealogyData = useMemo(() => ({ trees }), [trees])

  if (loading || !groups) {
    return <CenteredLoadingSpinner />
  }

  return (
    <LayoutBand marginY="md">
      <H1 size="H4">{t('tree-import.manage-trees.heading')}</H1>
      <Separator size="xs" />
      <AllTreesData>
        <ManageTreesContent isMobile={isMobile} canAccessCET={canAccessCET} canAccessFGT={canAccessFGT} />
      </AllTreesData>
      {canAccessCET && trees?.length > 0 && (
        <GenealogyTreesProvider genealogyTreesData={genealogyData}>
          <Card margins="xs">
            <H2 size="H5">{t('tree-import.manage-trees.genealogy-trees-heading')}</H2>
            <Separator size="xs" />
            {isMobile ? <GenealogyMobileTable /> : <GenealogyTable />}
          </Card>
        </GenealogyTreesProvider>
      )}
    </LayoutBand>
  )
}
